import * as React from 'react';
import {
  DefaultButton, Icon, Link, MessageBar, MessageBarType, Panel, PanelType, PrimaryButton, ProgressIndicator, Spinner, SpinnerSize
} from '@fluentui/react';
import styles from './SharePointOrganizer.module.scss';
import type { IBatchResult, IDryRunResult, IOperationLogEntry } from '../../../models/Operation';
import type { IOrganizationPlan } from '../../../models/Plan';
import type { IScanResult } from '../../../models/ScanResult';
import type { IResolvedChange } from '../../../plan/PlanSimulator';
import type { ChangeEngine } from '../../../engine/ChangeEngine';
import { describeGraphError } from '../../../utils/errors';
import type { ITelemetry } from '../../../utils/telemetry';
import { formatNumber } from '../../../utils/format';
import { CHANGE_LABEL, changeTitle } from './planDisplay';

export interface IApplyPanelProps {
  scan: IScanResult;
  plan: IOrganizationPlan;
  /** Approved changes with no validation errors. */
  changes: IResolvedChange[];
  engine: ChangeEngine;
  auditLogUrl: string;
  telemetry: ITelemetry;
  onDismiss: () => void;
  onRescan: () => void;
}

type Phase = 'checking' | 'review' | 'applying' | 'done' | 'error';

function plural(n: number, one: string, many: string): string {
  return `${formatNumber(n)} ${n === 1 ? one : many}`;
}

/** Dry run → confirmation → apply with progress → results. */
export default function ApplyPanel(props: IApplyPanelProps): React.ReactElement {
  const { scan, plan, changes, engine, auditLogUrl, telemetry, onDismiss, onRescan } = props;
  const [phase, setPhase] = React.useState<Phase>('checking');
  const [dryRun, setDryRun] = React.useState<IDryRunResult[]>([]);
  const [progress, setProgress] = React.useState<{ done: number; total: number }>({ done: 0, total: 0 });
  const [result, setResult] = React.useState<IBatchResult | undefined>(undefined);
  const [error, setError] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    let active: boolean = true;
    engine.dryRun(changes)
      .then(results => {
        telemetry.event('apply.dryRun', {
          libraryId: scan.library.id,
          ready: results.filter(r => r.ready).length,
          blocked: results.filter(r => !r.ready).length,
          operations: results.reduce((sum, r) => sum + r.operationCount, 0)
        });
        if (active) {
          setDryRun(results);
          setPhase('review');
        }
      })
      .catch(e => {
        telemetry.error('apply.dryRunFailed', e, { libraryId: scan.library.id });
        if (active) {
          setError(describeGraphError(e));
          setPhase('error');
        }
      });
    return () => { active = false; };
  }, [engine, changes, telemetry, scan]);

  const byId: { [changeId: string]: IResolvedChange } = {};
  for (const c of changes) {
    byId[c.change.id] = c;
  }
  const ready: IDryRunResult[] = dryRun.filter(d => d.ready);
  const blocked: IDryRunResult[] = dryRun.filter(d => !d.ready);
  const operations: number = ready.reduce((sum, d) => sum + d.operationCount, 0);

  const apply = (): void => {
    setPhase('applying');
    const readyChanges: IResolvedChange[] = ready.map(d => byId[d.changeId]);
    setProgress({ done: 0, total: engine.countOperations(readyChanges) });
    telemetry.event('apply.started', { libraryId: scan.library.id, changes: readyChanges.length });
    engine.apply(readyChanges, (done, total) => setProgress({ done, total }))
      .then(r => {
        const count = (status: string): number => r.entries.filter(e => e.status === status).length;
        telemetry.event('apply.finished', {
          libraryId: scan.library.id,
          batchId: r.batchId,
          succeeded: count('success'),
          skipped: count('skipped'),
          failed: count('failed'),
          aborted: !!r.abortedReason
        });
        setResult(r);
        setPhase('done');
      })
      .catch(e => {
        telemetry.error('apply.failed', e, { libraryId: scan.library.id });
        setError(describeGraphError(e));
        setPhase('error');
      });
  };

  const entries: IOperationLogEntry[] = result ? result.entries : [];
  const count = (status: IOperationLogEntry['status']): number =>
    entries.filter(e => e.status === status && e.action !== 'createFolder').length;
  const failed: IOperationLogEntry[] = entries.filter(e => e.status === 'failed');

  const footer = (): React.ReactElement => {
    switch (phase) {
      case 'review':
        return (
          <div className={styles.changeActions}>
            <PrimaryButton
              text={`Apply ${plural(ready.length, 'change', 'changes')}`}
              iconProps={{ iconName: 'Play' }}
              disabled={ready.length === 0}
              onClick={apply}
            />
            <DefaultButton text="Cancel" onClick={onDismiss} />
          </div>
        );
      case 'done':
      case 'error':
        return (
          <div className={styles.changeActions}>
            <PrimaryButton text="Rescan library" iconProps={{ iconName: 'Refresh' }} onClick={onRescan} />
            <DefaultButton text="Close" onClick={onDismiss} />
          </div>
        );
      default:
        return <></>;
    }
  };

  return (
    <Panel
      isOpen
      type={PanelType.medium}
      headerText="Apply approved changes"
      // Closing mid-batch would hide progress; the batch itself would still finish.
      isBlocking={phase === 'applying'}
      hasCloseButton={phase !== 'applying'}
      onDismiss={phase === 'applying' ? undefined : onDismiss}
      onRenderFooterContent={footer}
      isFooterAtBottom
    >
      {phase === 'checking' && (
        <Spinner size={SpinnerSize.large} label={`Checking ${plural(changes.length, 'change', 'changes')} against SharePoint…`} />
      )}

      {phase === 'review' && (
        <>
          <p>
            Every change was checked against the library as it is right now. Nothing has been changed yet.
          </p>
          <MessageBar messageBarType={ready.length > 0 ? MessageBarType.success : MessageBarType.warning} isMultiline>
            {plural(ready.length, 'change is', 'changes are')} ready ({plural(operations, 'operation', 'operations')} in SharePoint).
            {blocked.length > 0 && ` ${plural(blocked.length, 'change', 'changes')} can't be applied and will be skipped.`}
          </MessageBar>
          <ul className={styles.applyNotes}>
            <li><Icon iconName="Lock" /> Items are only moved or renamed. Quarantined items go to <strong>{plan.quarantinePath}</strong> in {scan.library.name}; nothing is deleted.</li>
            <li><Icon iconName="History" /> Every operation is recorded in the <Link href={auditLogUrl} target="_blank" rel="noreferrer">audit log</Link> with enough detail to undo it.</li>
            <li><Icon iconName="Shield" /> If an item changed since the scan, or its destination name is taken, it is left alone.</li>
            <li><Icon iconName="Sync" /> People who sync this library with OneDrive will see the changes on their computers shortly afterwards{operations > 200 ? ' — with this many changes, syncing may take a while' : ''}.</li>
          </ul>
          {blocked.length > 0 && (
            <>
              <h4 className={styles.applyHeading}>Will be skipped</h4>
              <ul className={styles.applyList}>
                {blocked.map(d => (
                  <li key={d.changeId}>
                    <strong>{CHANGE_LABEL[byId[d.changeId].change.type]}: {changeTitle(scan, byId[d.changeId])}</strong>
                    {d.problems.map(p => <div key={p} className={styles.applyProblem}>{p}</div>)}
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}

      {phase === 'applying' && (
        <ProgressIndicator
          label="Applying changes…"
          description={`${formatNumber(progress.done)} of ${formatNumber(progress.total)} operations. Keep this page open.`}
          percentComplete={progress.total ? progress.done / progress.total : undefined}
        />
      )}

      {phase === 'done' && result && (
        <>
          {result.abortedReason && <MessageBar messageBarType={MessageBarType.severeWarning} isMultiline>{result.abortedReason}</MessageBar>}
          <MessageBar messageBarType={failed.length || result.abortedReason ? MessageBarType.warning : MessageBarType.success} isMultiline>
            {plural(count('success'), 'operation', 'operations')} succeeded
            {count('skipped') > 0 && `, ${formatNumber(count('skipped'))} already done`}
            {failed.length > 0 && `, ${formatNumber(failed.length)} failed`}.
          </MessageBar>
          <p>
            Batch <code>{result.batchId}</code>. <Link href={auditLogUrl} target="_blank" rel="noreferrer">Open the audit log</Link>.
            The library has changed, so rescan it before making more changes.
          </p>
          {failed.length > 0 && (
            <>
              <h4 className={styles.applyHeading}>Not changed</h4>
              <ul className={styles.applyList}>
                {failed.map(e => (
                  <li key={e.operationId}>
                    <strong>{e.sourcePath || e.destinationPath}</strong>
                    <div className={styles.applyProblem}>{e.errorMessage}</div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}

      {phase === 'error' && (
        <MessageBar messageBarType={MessageBarType.error} isMultiline>{error}</MessageBar>
      )}
    </Panel>
  );
}
